"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createHeartbeatSender = createHeartbeatSender;
exports.default = void 0;
/**
 * Telling the server that a person is here.
 *
 * The idle cookie only works while a browser is running. Close a laptop at
 * minute five and nothing revokes anything, and the Hanko session stays valid
 * for up to a day. This is what lets the server enforce the same 30-minute rule
 * on its own: sam-dbam records the time and refuses requests that arrive too
 * long after it.
 *
 * Three rules this follows, and each of them matters:
 *
 * 1. **It is sent on real activity only.** Never on a timer. A heartbeat sent
 *    by a background poll would report somebody who is not there, and the
 *    server-side check would become decoration. The clock calls this only when
 *    a person pressed a key, clicked, scrolled or touched the screen.
 *
 * 2. **It never throws and never reports.** A failed heartbeat is a backstop
 *    that missed, not something the person can act on. Showing an error, or
 *    letting one bubble into the app, would turn a silent safety net into a
 *    visible fault.
 *
 * 3. **It never retries.** The next real action sends another one. Retrying a
 *    stale "somebody was here a minute ago" is worse than saying nothing.
 */

/**
 * Build the function the idle clock calls when it is time to report activity.
 *
 * @param {object} options
 * @param {string|function} options.url  the endpoint, or a function returning
 *   it (possibly a promise) for apps that resolve their service addresses at
 *   run time.
 * @param {function} [options.fetchImpl]  injected for tests.
 * @returns {function(): Promise<void>} never rejects.
 */
function createHeartbeatSender({
  url,
  fetchImpl
} = {}) {
  const send = fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
  if (!url || !send) {
    // Nothing to call, or nothing to call it with. The browser-side clock
    // still works; only the server backstop is absent.
    return () => Promise.resolve();
  }
  return async function sendHeartbeat() {
    try {
      const endpoint = typeof url === 'function' ? await url() : url;
      if (!endpoint) return;
      await send(endpoint, {
        method: 'POST',
        credentials: 'include',
        // The tab may be closing as this goes out. keepalive lets the
        // request finish anyway, which is exactly the case the server
        // backstop exists for.
        keepalive: true
      });
    } catch (err) {
      // Deliberately silent. See rule 2 above.
    }
  };
}
var _default = exports.default = createHeartbeatSender;